import { InMemoryCache } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { from, split } from 'apollo-link';
import { SchemaLink } from 'apollo-link-schema';
import { addResolveFunctionsToSchema } from 'graphql-tools';
import { getCustomResolversFetch } from './helpers/fetch';
import { isCustomQuery } from './helpers/links';
import { createDirectoryHttpLink } from './links/directory-http';
import { createErrorLink } from './links/error';
import { createRetryLink } from './links/retry';
import resolvers from './resolvers';
import { schema } from './schema';
export const createApolloClient = options => {
  const {
    retryLink: retryOptions = {},
    getCloudId,
    url,
    statusCodeHandlers = {},
    customFetch
  } = options;

  // Build up a list of Apollo links
  const links = [];

  /**
   * Apollo link for handling request errors
   */
  const errorLink = createErrorLink({
    statusCodeHandlers
  });
  if (errorLink) {
    links.push(errorLink);
  }

  /**
   * Apollo link for retrying certain failed requests
   */

  const retryLink = createRetryLink(retryOptions);
  if (retryLink) {
    links.push(retryLink);
  }

  /**
   * Apollo link that determines whether to send a request to directory
   * service or resolve the query locally using a custom resolver
   */
  const customResolversLink = new SchemaLink({
    schema: addResolveFunctionsToSchema({
      schema,
      resolvers
    }),
    context: {
      url,
      fetch: getCustomResolversFetch({
        fetch: customFetch || fetch,
        statusCodeHandlers
      })
    }
  });
  const directoryHttpLink = createDirectoryHttpLink({
    url: `${url ? url.stargateRoot : ''}/directory/graphql`,
    getCloudId
  });
  links.push(split(
  // if query has @custom, use customResolversLink
  operation => isCustomQuery(operation), customResolversLink,
  // use directory link
  directoryHttpLink));
  return new ApolloClient({
    connectToDevTools: !!options.connectToDevTools,
    link: from(links),
    cache: new InMemoryCache(),
    // empty object enables @client directive
    resolvers: {}
  });
};
export const createWorklensClient = options => {
  const {
    retryLink: retryOptions = {},
    url,
    statusCodeHandlers = {}
  } = options;
  const errorLink = createErrorLink({
    statusCodeHandlers
  });
  const retryLink = createRetryLink(retryOptions);
  const httpLink = createDirectoryHttpLink({
    url: `${url.stargateRoot}/worklens/api/graphql`
  });
  const links = [errorLink, retryLink, httpLink];
  return new ApolloClient({
    link: from(links),
    cache: new InMemoryCache()
  });
};
export const createAtlassianGatewayClient = options => {
  const {
    retryLink: retryOptions = {},
    url,
    statusCodeHandlers = {}
  } = options;
  const errorLink = createErrorLink({
    statusCodeHandlers
  });
  const retryLink = createRetryLink(retryOptions);
  const httpLink = createDirectoryHttpLink({
    url: `${url.stargateRoot}/graphql`
  });
  const links = [errorLink, retryLink, httpLink];
  return new ApolloClient({
    link: from(links),
    cache: new InMemoryCache(),
    // empty object enables @client directive
    resolvers: {}
  });
};
export const createTeamCentralClient = options => {
  const {
    retryLink: retryOptions = {},
    url,
    statusCodeHandlers = {}
  } = options;
  const errorLink = createErrorLink({
    statusCodeHandlers
  });
  const retryLink = createRetryLink(retryOptions);
  const httpLink = createDirectoryHttpLink({
    url: `${url ? url.stargateRoot : ''}/watermelon/graphql`
  });
  const links = [errorLink, retryLink, httpLink];
  return new ApolloClient({
    link: from(links),
    cache: new InMemoryCache({
      dataIdFromObject: obj => {
        switch (obj.__typename) {
          case 'Icon':
            return `${obj.id}:${obj.shortName}|${obj.color}`;
          case 'GoalPhaseDetails':
          case 'ProjectPhaseDetails':
          case 'GqlJiraIssue':
          case 'GqlJiraIssueType':
          case 'GqlJiraStatus':
          case 'GqlJiraStatusCategory':
            return `${obj.__typename}:${obj.id}`;
          default:
            return obj.id;
        }
      }
    })
  });
};